import moment from 'moment';
import { Box } from '@chakra-ui/react';
import Actions, { IAction } from 'components/DataTable/Actions';
import { ActionLoadingCallback } from 'components/DataTable/Actions/Action';
import { useConfirm } from 'contexts';

(window as any).moment = moment;

export type CellProps = {
  dateFormat?: string;
};

export type ArrayRendererProps = {
  value: string[];
};

export type BooleanRendererProps = {
  value?: unknown;
};

export type EnumRendererRenderProps = {
  value: string;
};

export type DateRendererProps = {
  value: string;
  cellProps: CellProps;
};

type InlineActionTypeCallback<T extends object> = (data: T, cb: ActionLoadingCallback) => void;

type IInlineAction<T extends object> = Omit<IAction<T>, 'callback'> & {
  callback: (data: T, item: T, cb: (isLoading: boolean) => void) => void;
};

type ActionsRendererProps<T extends object> = {
  data: T;
  actions: IInlineAction<T>[];
};

type IShowConfirmMessageParams = {
  confirmText?: string;
  message: string;
};

export const booleanRenderer = ({ value }: BooleanRendererProps) => (value ? 'Yes' : 'No');

export const dateRenderer = ({ value, cellProps: { dateFormat } }: DateRendererProps) => {
  const formatted = moment(value).format(dateFormat);
  return value ? formatted : '';
};

export const enumRenderer = ({ value }: EnumRendererRenderProps, map: Record<string, unknown>, fallback: string) =>
  map[value] ?? fallback;

export const arrayRenderer = ({ value }: ArrayRendererProps) =>
  value && Array.isArray(value) && value.length > 0 ? value.join(', ') : null;

export const ActionsRenderer = <T extends object>({ data, actions }: ActionsRendererProps<T>) => {
  const [showConfirm] = useConfirm();

  const handleShowConfirm = (
    callback: (item: T, cb: ActionLoadingCallback) => void,
    item: IAction<T>,
    cb: ActionLoadingCallback,
    { confirmText = 'CONFIRM', message }: IShowConfirmMessageParams
  ) => {
    showConfirm({
      onConfirm: () => callback(data, cb),
      confirmText,
      title: item.renderTitle ? item.renderTitle(data) : undefined,
      message: item.renderMessage ? item.renderMessage(data) : message,
    });
  };

  const handleCallback = (
    item: T,
    callback: (itemData: T, callbackData: T, cb: ActionLoadingCallback) => void,
    cb: ActionLoadingCallback
  ) => {
    callback(data, item, cb);
  };

  return (
    <Box pr={4}>
      <Actions<T>
        items={actions
          .filter((action: IInlineAction<T>) => !action.condition || action.condition(data))
          .map((action: IInlineAction<T>) => ({
            ...action,
            callback: (item: T, cb: ActionLoadingCallback) => {
              handleCallback(item, action.callback, cb);
            },
            actionProps: {
              sx: {
                px: 2,
                minW: 0,
                w: 8,
                h: 7,
                fontSize: 'sm',
              },
            },
          }))}
        callbacks={{
          clone: (callback: InlineActionTypeCallback<T>, item: IAction<T>, cb: ActionLoadingCallback) =>
            handleShowConfirm(callback, item, cb, {
              message: 'Are you sure you want to clone this item?',
            }),
          delete: (callback: InlineActionTypeCallback<T>, item: IAction<T>, cb: ActionLoadingCallback) =>
            handleShowConfirm(callback, item, cb, {
              message: 'You are about to permanently delete this item, do you want to proceed?',
            }),
          approve: (callback: InlineActionTypeCallback<T>, item: IAction<T>, cb: ActionLoadingCallback) =>
            handleShowConfirm(callback, item, cb, {
              message: 'You are about to approve this item, do you want to proceed?',
            }),
          deny: (callback: InlineActionTypeCallback<T>, item: IAction<T>, cb: ActionLoadingCallback) =>
            handleShowConfirm(callback, item, cb, {
              message: 'You are about to deny this item, do you want to proceed?',
            }),
        }}
      />
    </Box>
  );
};
